import React, { memo, useEffect, useMemo } from 'react'

import { commonGridColDefs } from '@/entities/columnDefinitions/commonColDefs'
import { getGridColDefs } from '@/entities/columnDefinitions/libs/getColDef'
import { setPlanningShiftTableLoading } from '@/entities/gridTables/slices/planningShiftTablesSlice'
import { setPlanningUnassignedTableLoading } from '@/entities/gridTables/slices/planningUnassignedTablesSlice'
import { moveBreak } from '@/entities/gridTables/thunks/moveBreak'
import { processDropOnShift, ProcessDropOnShiftPayload } from '@/entities/gridTables/thunks/processDropOnShift'
import { updatePlanningShiftTableState } from '@/entities/gridTables/thunks/updatePlanningShiftTableState'
import { updatePlanningUnassignedTableState } from '@/entities/gridTables/thunks/updatePlanningUnassignedTableState'
import { AssignedToScenario } from '@/entities/orders/models/Order'
import { fetchPlanningOrdersByShift } from '@/entities/orders/thunks/fetchPlanningOrdersByShift'
import { fetchScenarios, planningShiftsSelectors, scenariosSelectors, StopTypeEnum } from '@/entities/schedules'
import { selectBusinessModel } from '@/entities/user'
import { BusinessModelEnum } from '@/entities/user/models/BusinessModel'
import { openRightSidebar, setLastSelectedStopId } from '@/entities/userInterface/slices/userInterfaceSlice'
import { setDetailViewedOrder } from '@/entities/userInterface/thunks/setDetailViewedOrder'
import { upsertOneWidgetWindow } from '@/entities/widgetWindows'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { GridRowActionItem } from '@/shared/components/GridTable/models/GridRowActions'
import { GridTableDragType } from '@/shared/components/GridTable/models/GridTableDragType'
import { TREE_WIDGETS, WidgetTypeWithSuffix } from '@/shared/components/Mosaic/models/widgetType'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { faCarSide, faTruck, faUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons'
import { Typography } from '@mui/material'
import { GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-premium'
import { EntityId } from '@reduxjs/toolkit'

import { useColumnGroupingModel } from './libs/useColumnGroupingModel'
import { usePlanningShiftTableData } from './libs/usePlanningShiftTableData'
import { usePlanningShiftTableContext } from './PlanningShiftTableContext'

export type PlanningShiftTableProps = {
  widgetId: WidgetTypeWithSuffix // Widget Window id
  id: string // Planning Shift id
}

// TODO:
//   1: Optimize Grid further by preventing it from being re-rendered when the parent window changes
//   2: Use React Portal to move the component around without significant re-rendering
export const PlanningShiftTable: React.FC<PlanningShiftTableProps> = memo(({ widgetId, id }) => {
  const { planningShift, rows, isLoading } = usePlanningShiftTableData(id)
  const { columnGroupingModel, initialColumnGroupVisibility } = useColumnGroupingModel()
  const { setShiftId, setScenarioId } = usePlanningShiftTableContext()
  const businessModel = useAppSelector(selectBusinessModel)
  const shift = useAppSelector(state => planningShiftsSelectors.selectById(state, id))
  const scenarioName = useAppSelector(state => scenariosSelectors.selectById(state, shift?.scenarioId || '')?.name)
  const selectedSearchResult = useAppSelector(state => state.userInterface.selectedSearchResult)
  const columns = getGridColDefs({
    gridTypeName: 'planningShift',
    additionalColDefs: [commonGridColDefs.geocodeError],
  })
  const dispatch = useAppDispatch()
  const apiRef = useGridApiRef()

  // Update global store with widow title, uiColor, etc.
  useEffect(() => {
    if (planningShift) {
      dispatch(upsertOneWidgetWindow({
        id: widgetId,
        isActive: true,
        widgetType: TREE_WIDGETS.PlanningShiftTable,
        scenarioId: planningShift.scenarioId,
        shiftId: id,
        navigations: [
          {
            title: scenarioName || '',
          },
          {
            title: planningShift.name,
            icon: (businessModel === BusinessModelEnum.Depot) ? faTruck : faCarSide,
            uiColor: planningShift.uiColor || 'black',
          },
        ],
      }))
    }
  }, [dispatch, widgetId, scenarioName, planningShift, businessModel, id])

  // Update Selected rows when search result change
  useEffect(() => {
    if (apiRef.current && selectedSearchResult && selectedSearchResult.isPlanning && selectedSearchResult.shiftId == id &&
      selectedSearchResult.stopId && apiRef?.current?.getRow(selectedSearchResult.stopId)) {
      apiRef.current?.selectRows([selectedSearchResult.stopId], true, true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef.current, selectedSearchResult, id])

  // TODO: Update API Endpoint to retrieve orders directly from shift id instead of OrderIds and call it before rendering this component if possible
  useEffect(() => {
    dispatch(fetchPlanningOrdersByShift({ shiftId: id }))
  }, [dispatch, id])

  useEffect(() => {
    setShiftId(parseInt(id))
    setScenarioId(shift?.scenarioId)
  }, [id, setShiftId, setScenarioId, shift])

  const onGridRowClick = (selectedRowData: GridValidRowModel) => {
    dispatch(setLastSelectedStopId(selectedRowData.id))
  }

  const onGridRowsDrop = async (sourceRowData: GridValidRowModel[], targetRowData: GridValidRowModel) => {
    const sourceType = sourceRowData[0]?.dragType
    const isSourceBreak = sourceRowData[0]?.stopType === StopTypeEnum.Break
    const sourceScenarioId = sourceRowData[0].scenarioId
    const isSameScenario = sourceScenarioId == targetRowData.scenarioId
    const refreshUnassignedOrders = sourceType === GridTableDragType.UNASSIGNED_ORDER_ROW && isSameScenario

    const shiftTablesToReload: EntityId[] = []
    let payload: ProcessDropOnShiftPayload | null = null

    // Construct Payload
    if (sourceType === GridTableDragType.SHIFT_STOP_ROW) {
      const sourceShiftId = sourceRowData[0].shiftId
      const targetShiftId = targetRowData.shiftId
      const sourceStopIds = sourceRowData.map((row) => row.id)
      const targetStopId = targetRowData.id

      shiftTablesToReload.push(sourceShiftId)
      shiftTablesToReload.push(targetShiftId)

      payload = {
        orderIds: null,
        sourceShiftId,
        shiftId: targetShiftId,
        stopId: targetStopId,
        stopIds: sourceStopIds,
      }
    } else if (sourceType === GridTableDragType.INBOX_ORDER_ROW) {
      const orderIds = sourceRowData.map((row) => row.orderId)
      const targetShiftId = targetRowData.shiftId
      const targetStopId = targetRowData.id

      shiftTablesToReload.push(targetShiftId)

      let i = 0

      for (i = 0; i < sourceRowData.length; i++) {
        const row = sourceRowData[i]
        const assignedToScenario = (row.scenarios as AssignedToScenario[]).find(item => item.scenarioId === targetRowData.scenarioId)

        if (assignedToScenario) {
          shiftTablesToReload.push(assignedToScenario.shiftId)
        }
      }

      payload = {
        orderIds,
        sourceShiftId: null,
        shiftId: targetShiftId,
        stopId: targetStopId,
        stopIds: null,
      }
    } else if (sourceType === GridTableDragType.UNASSIGNED_ORDER_ROW) {
      const orderIds = sourceRowData.map((row) => row.orderId)
      const targetShiftId = targetRowData.shiftId
      const targetStopId = targetRowData.id

      shiftTablesToReload.push(targetShiftId)

      payload = {
        orderIds,
        sourceShiftId: null,
        shiftId: targetShiftId,
        stopId: targetStopId,
        stopIds: null,
      }
    }

    if (!payload) return

    // Send Payload
    // Set loading state
    shiftTablesToReload.forEach(shiftId => {
      dispatch(setPlanningShiftTableLoading({ id: shiftId, isLoading: true }))
    })

    if (refreshUnassignedOrders) {
      dispatch(setPlanningUnassignedTableLoading({ id: sourceRowData[0].scenarioId, isLoading: true }))
    }

    if (isSourceBreak) {
      await dispatch(moveBreak({ stopId: sourceRowData[0].id, targetStopId: targetRowData.id }))
      await dispatch(fetchScenarios({ scenarioIds: [sourceRowData[0].scenarioId] }))
    } else {
      // We only need to update the target shift table state on planning screen
      //    because we are copying the orders from source shift to target shift
      await dispatch(processDropOnShift(payload))
      await dispatch(fetchScenarios({ scenarioIds: [targetRowData.scenarioId, sourceRowData[0].scenarioId] }))
    }

    // Refresh the relevant tables
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const shiftTablesReloadActions: any = shiftTablesToReload.map(shiftId => dispatch(updatePlanningShiftTableState({ shiftId: shiftId })))

    if (refreshUnassignedOrders) {
      shiftTablesReloadActions.push(dispatch(updatePlanningUnassignedTableState({ scenarioId: sourceRowData[0].scenarioId })))
    }

    await Promise.allSettled(shiftTablesReloadActions)

    // Reset loading state
    shiftTablesToReload.forEach(shiftId => {
      dispatch(setPlanningShiftTableLoading({ id: shiftId, isLoading: false }))
    })

    if (refreshUnassignedOrders) {
      dispatch(setPlanningUnassignedTableLoading({ id: sourceRowData[0].scenarioId, isLoading: false }))
    }
  }

  const viewOrderDetailAction: GridRowActionItem = {
    icon: faUpRightFromSquare,
    label: 'View Order',
    onClick: (rowData: GridValidRowModel) => {
      if (rowData.orderId) {
        dispatch(openRightSidebar())
        dispatch(setDetailViewedOrder({ orderId: rowData.orderId }))
      }
    },
  }

  const footerSummary = useMemo(() => (
    <React.Fragment>
      Total:&nbsp;
      Orders: <strong>{planningShift?.totalQuantity}</strong>&nbsp;&nbsp;
      Dist: <strong>{planningShift?.totalDistance}</strong>&nbsp;&nbsp;
      Cost: <strong>{planningShift?.totalCost}</strong>&nbsp;&nbsp;
      Wgt: <strong>{planningShift?.totalWeight}</strong>&nbsp;&nbsp;
      Vol: <strong>{planningShift?.totalVolume}</strong>&nbsp;&nbsp;
      Time: <strong>{planningShift?.totalWorkingTime}</strong>&nbsp;&nbsp;
    </React.Fragment>
  ), [planningShift?.totalQuantity, planningShift?.totalDistance, planningShift?.totalCost,
    planningShift?.totalVolume, planningShift?.totalWeight, planningShift?.totalWorkingTime])

  if (!rows) {
    return <Typography variant="body1">Loading...</Typography>
  }

  return (
    <GridTable
      apiRef={apiRef}
      rows={rows}
      columns={columns}
      columnGroupingModel={columnGroupingModel}
      checkboxSelection
      isDragDropEnabled
      isRowActionsEnabled
      loading={isLoading}
      disableVirtualization
      onRowClick={onGridRowClick}
      onGridRowsDrop={onGridRowsDrop} // Drag and Drop
      pinnedLeftColumns={['geocodeError']}
      hotAction={viewOrderDetailAction}
      initialColumnGroupVisibility={initialColumnGroupVisibility}
      slotProps={{
        footer: {
          summary: footerSummary,
        },
      }}
    />
  )
}, (prevProps, nextProps) => {
  return prevProps.id === nextProps.id && prevProps.widgetId === nextProps.widgetId
})

